import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const Feature = ({ icon, title, description }) => (
  <Paper 
    elevation={0} 
    sx={{ 
      p: 2, 
      mb: 2,
      backgroundColor: '#F5F8FA',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box 
        sx={{ 
          p: 1,
          borderRadius: 1,
          backgroundColor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main' }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const HelpModal = ({ open, onClose }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle 
        sx={{ 
          fontFamily: 'Merriweather, Georgia, serif',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontFamily: 'inherit', mb: 1 }}>
            Welcome to FeedTahoe SMS Dispatch Tool
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A guide to help you get started with the messaging system
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        

        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          Key Features
        </Typography>

        <Feature
          icon={<SearchIcon />}
          title="Search Conversations"
          description="Use the search bar at the top of the conversation list to filter conversations. Click the info (i) button next to the search bar for advanced search tips and features."
        />

        <Feature
          icon={<AddIcon />}
          title="Start New Conversations"
          description="Click the plus (+) button in the top right to start a new conversation. Enter the contact's phone number and name to begin messaging."
        />

        <Feature
          icon={<PersonAddIcon />}
          title="Manage Contacts"
          description="When viewing a conversation, click the person icon in the top right to add or edit contact information. You can categorize contacts as donors, nonprofits, volunteers, or others."
        />

        <Feature
          icon={<SettingsIcon />}
          title="Settings & Username"
          description="Click the gear icon to access settings. Important: You must set your username before sending messages. This helps others identify who sent each message."
        />

        <Feature
          icon={<InfoOutlinedIcon />}
          title="Welcome Message Templates"
          description={
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                When starting a new conversation, you'll find three welcome message templates, each designed for a specific contact type:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(74, 175, 80, 0.08)', 
                  border: '1px solid',
                  borderColor: '#4CAF50'
                }}>
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 500 }}>
                    🤝 Volunteer Welcome
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Includes volunteer guide and first rescue instructions
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(1, 88, 135, 0.08)',
                  border: '1px solid',
                  borderColor: '#015887'
                }}>
                  <Typography variant="body2" sx={{ color: '#015887', fontWeight: 500 }}>
                    🏪 Donor Welcome
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Includes donor information and food rescue process details
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(248, 151, 28, 0.08)',
                  border: '1px solid',
                  borderColor: '#F8971C'
                }}>
                  <Typography variant="body2" sx={{ color: '#F8971C', fontWeight: 500 }}>
                    🏢 Nonprofit Welcome
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Includes nonprofit partner guide and delivery information
                  </Typography>
                </Box>
              </Box>
            </Box>
          }
        />

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.light', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            Pro Tips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Use the search prefix "all:" to search through message content (e.g., "all:delivery")<br />
            • Press Enter to send messages quickly<br />
            • Click on any conversation to view its full message history<br />
            • Your username will be visible to others when you send messages
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HelpModal; 